import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { enUS } from "@mui/x-date-pickers/locales";

import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import {
  AdvancedDdlItems,
  DdlResult,
  Operator,
  ReportTemplateTypeEnum,
} from "../../../../services/api/types/reports/reports";
import {
  StringOperator,
  NumericOperator,
  DateOperator,
  BooleanOperator,
  ListOperator,
} from "../../../../services/api/types/users/users";
import { DynamicFieldRenderer } from "../../../../components/DynamicFieldRenderer/DynamicFieldRenderer";
import VisibilityIcon from "@mui/icons-material/Visibility";

const activityDateFormat = "DD-MMM-YYYY";
const commonLocaleText = {
  ...enUS.components.MuiLocalizationProvider.defaultProps.localeText,
  fieldMonthPlaceholder: (params: any) =>
    params.contentType === "letter" ? "MMM" : "MM",
};

interface UserFiltersProps {
  value: {
    userFilters: Array<{
      attribute: string;
      operator: Operator | "";
      value: string;
    }>;
    activityType: string[];
    activityDate: string;
  };
  userAttributeOptions: AdvancedDdlItems[];
  userActionTypeOptions: DdlResult | null;
  reportType: string;
  isExpanded: boolean;
  getValuesForAttribute: (
    attribute: string,
    operator:
      | StringOperator
      | NumericOperator
      | DateOperator
      | BooleanOperator
      | ListOperator
      | "",
    value: string,
    filterIndex: number,
  ) => void;
  onChange: (data: {
    userFilters?: Array<{
      attribute: string;
      operator: Operator | "";
      value: string;
    }>;
    activityType?: string[];
    activityDate?: string;
  }) => void;
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  value,
  userAttributeOptions,
  userActionTypeOptions,
  getValuesForAttribute,
  onChange,
  isExpanded,
  reportType,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [openDatepicker, setOpenDatepicker] = useState<boolean[]>([]);
  const [openStartDatepicker, setOpenStartDatepicker] = useState<boolean[]>([]);
  const [openEndDatepicker, setOpenEndDatepicker] = useState<boolean[]>([]);
  const [openActivityStart, setOpenActivityStart] = useState(false);
  const [openActivityEnd, setOpenActivityEnd] = useState(false);

  const { customisations } = useCustomisation();
  const {
    GPC: { textColorsSettings },
  } = customisations.customisations;

  const primaryTextColor = textColorsSettings?.gpcPrimaryTextColor;

  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  // Clear activityType when report type is User report or Assignment activity report
  useEffect(() => {
    if (
      reportType &&
      (reportType === ReportTemplateTypeEnum.USER_REPORT ||
        reportType === ReportTemplateTypeEnum.ASSIGNMENT_ACIVITY_REPORT)
    ) {
      if (value.activityType && value.activityType.length > 0) {
        onChange({ activityType: [] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const filters =
    value.userFilters.length > 0
      ? value.userFilters
      : [
          {
            attribute: "",
            operator: "" as Operator | "",
            value: "",
          },
        ];

  const handleAddRow = () => {
    const updatedFilters: Array<{
      attribute: string;
      operator: Operator | "";
      value: string;
    }> = [
      ...filters,
      { attribute: "", operator: "" as Operator | "", value: "" },
    ];

    onChange({ userFilters: updatedFilters });

    setOpenDatepicker((prev) => [...prev, false]);
    setOpenStartDatepicker((prev) => [...prev, false]);
    setOpenEndDatepicker((prev) => [...prev, false]);
  };

  const handleRemoveRow = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);

    onChange({
      userFilters:
        updatedFilters.length > 0
          ? updatedFilters
          : [{ attribute: "", operator: "" as Operator | "", value: "" }],
    });

    setOpenDatepicker((prev) => prev.filter((_, i) => i !== index));
    setOpenStartDatepicker((prev) => prev.filter((_, i) => i !== index));
    setOpenEndDatepicker((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: string, newValue: string) => {
    const updated = filters.map((f, i) => {
      if (i === index) {
        // If attribute changes, clear operator and value
        if (field === "attribute") {
          return {
            ...f,
            attribute: newValue,
            operator: "" as Operator | "",
            value: "",
          };
        }
        if (field === "operator") {
          return { ...f, operator: newValue as Operator | "" };
        }
        return { ...f, [field]: newValue };
      }
      return f;
    });
    onChange({ userFilters: updated });
  };

  // Get the selected attribute to show its operators/values
  const getSelectedAttribute = (attributeKey: string) => {
    return userAttributeOptions.find(
      (attr) => attr.attributeKey === attributeKey,
    );
  };

  // Get attribute data type
  const getAttributeDataType = (attributeKey: string): string | null => {
    const attr = getSelectedAttribute(attributeKey);
    return attr ? attr.dataType.toLowerCase() : null;
  };

  // Get operator enum based on attribute data type
  const getAttributeOperator = (
    attributeKey: string,
  ): Array<[string, string]> => {
    const dataType = getAttributeDataType(attributeKey)?.toLowerCase();

    switch (dataType) {
      case "string":
      case "text":
        return [
          ["IN", StringOperator.IN],
          ["NOT_IN", StringOperator.NOT_IN],
          ["CONTAINS", StringOperator.CONTAINS],
          ["NOT_CONTAINS", StringOperator.NOT_CONTAINS],
          ["STARTS_WITH", StringOperator.STARTS_WITH],
          ["ENDS_WITH", StringOperator.ENDS_WITH],
        ];
      case "number":
      case "integer":
      case "numeric":
        return Object.entries(NumericOperator) as Array<[string, string]>;
      case "date":
      case "datetime":
        return [
          ["EQUAL", DateOperator.EQUAL],
          ["NOT_EQUAL", DateOperator.NOT_EQUAL],
          ["LESS_THAN", DateOperator.LESS_THAN],
          ["GREATER_THAN", DateOperator.GREATER_THAN],
          ["GREATER_THAN_EQUAL", DateOperator.GREATER_THAN_EQUAL],
          ["LESS_THAN_EQUAL", DateOperator.LESS_THAN_EQUAL],
          ["BETWEEN", DateOperator.BETWEEN],
        ];
      case "boolean":
      case "bool":
        return Object.entries(BooleanOperator) as Array<[string, string]>;
      case "list":
      case "array":
        return Object.entries(ListOperator) as Array<[string, string]>;
      default:
        return [
          ["IN", StringOperator.IN],
          ["NOT_IN", StringOperator.NOT_IN],
          ["CONTAINS", StringOperator.CONTAINS],
          ["NOT_CONTAINS", StringOperator.NOT_CONTAINS],
          ["STARTS_WITH", StringOperator.STARTS_WITH],
          ["ENDS_WITH", StringOperator.ENDS_WITH],
        ];
    }
  };

  // Format enum key to readable label
  const getEnumLabel = (key: string, dataType?: string) => {
    const dt = dataType?.toLowerCase();

    // Custom labels for date operators
    if (dt === "date" || dt === "datetime") {
      const dateLabels: Record<string, string> = {
        EQUAL: "Equals",
        NOT_EQUAL: "Does not equal",
        LESS_THAN: "Is before",
        GREATER_THAN: "Is after",
        GREATER_THAN_EQUAL: "Is on or after",
        LESS_THAN_EQUAL: "Is on or before",
        BETWEEN: "Is between",
      };
      return (
        dateLabels[key] ||
        key
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/^./, (c) => c.toUpperCase())
      );
    }

    // Custom labels for string operators
    if (dt === "string" || dt === "text") {
      const stringLabels: Record<string, string> = {
        IN: "Is in",
        NOT_IN: "Is not in",
        CONTAINS: "Contains",
        NOT_CONTAINS: "Does not contain",
        STARTS_WITH: "Starts with",
        ENDS_WITH: "Ends with",
      };
      return (
        stringLabels[key] ||
        key
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/^./, (c) => c.toUpperCase())
      );
    }

    // Default formatting for other types
    return key
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());
  };

  const isStringAttribute = (attributeKey: string): boolean => {
    const attr = getSelectedAttribute(attributeKey);
    if (!attr) return false;
    return (
      attr.dataType.toLowerCase() === "string" ||
      attr.dataType.toLowerCase() === "text"
    );
  };

  // Helper function to check if activity type should be disabled
  const isActivityTypeDisabled = () => {
    if (!reportType) return false;
    // Check if report type matches "User report" or "Assignment activity report"
    return (
      reportType === ReportTemplateTypeEnum.USER_REPORT ||
      reportType === ReportTemplateTypeEnum.ASSIGNMENT_ACIVITY_REPORT
    );
  };

  const isUserActivityReport =
    reportType === ReportTemplateTypeEnum.USER_ACTIVITY_REPORT;

  const handleActivityDateChange = (
    field: "from" | "to",
    date: Dayjs | null,
  ) => {
    const formatted = date ? dayjs(date).format(activityDateFormat) : "";
    const [currentFrom, currentTo] = (value.activityDate || "").split("|");

    let nextFrom = currentFrom || "";
    let nextTo = currentTo || "";

    if (field === "from") {
      nextFrom = formatted;
    } else {
      nextTo = formatted;
    }

    const activityDate =
      nextFrom && nextTo ? `${nextFrom}|${nextTo}` : nextFrom || nextTo || "";

    onChange({ activityDate });
  };

  const [fromRaw, toRaw] = (value.activityDate || "").split("|");

  const fromDateValue = fromRaw ? dayjs(fromRaw, activityDateFormat) : null;
  const toDateValue = toRaw ? dayjs(toRaw, activityDateFormat) : null;

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        borderRadius: "12px",
        backgroundColor: "white",
        border: "1px solid #E5E7EB",
        boxShadow: "none",
        "&:before": { display: "none" }, // remove default divider line
        "&:not(:last-of-type)": {
          borderBottom: "1px solid #E5E7EB",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2.5,
          py: 1.5,
          minHeight: 64,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            margin: 0,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FilterAltOutlinedIcon sx={{ color: "#7C3AED", fontSize: 24 }} />
          <Typography fontSize={18} fontWeight={500}>
            User filters
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        <Box sx={{ borderTop: "1px solid #E5E7EB", pt: 2 }}>
          {filters.map((filter, index) => {
            const valueField = getSelectedAttribute(filter.attribute) ? (
              <DynamicFieldRenderer
                config={{
                  dataType: getSelectedAttribute(filter.attribute)!.dataType,
                  attributeKey: filter.attribute,
                  attributeName: getSelectedAttribute(filter.attribute)!.attributeName,
                }}
                sx={{
                  inputRoot: { minHeight: 44 },
                  datePicker: { height: 44 },
                  switchLabel: { height: 44 },
                }}
                value={filter.value}
                options={
                  getSelectedAttribute(filter.attribute)?.result?.map((opt) => ({
                    key: opt.key,
                    value: opt.value,
                  })) || []
                }
                onChange={(newValue) => {
                  handleChange(index, "value", String(newValue || ""));
                }}
                mode="filter"
                operator={
                  filter.operator as
                    | StringOperator
                    | NumericOperator
                    | DateOperator
                    | BooleanOperator
                    | ListOperator
                    | ""
                }
                openDatepicker={openDatepicker[index] || false}
                onOpenDatepicker={() =>
                  setOpenDatepicker((prev) => { const arr = [...prev]; arr[index] = true; return arr; })
                }
                onCloseDatepicker={() =>
                  setOpenDatepicker((prev) => { const arr = [...prev]; arr[index] = false; return arr; })
                }
                openStartDatepicker={openStartDatepicker[index] || false}
                onOpenStartDatepicker={() =>
                  setOpenStartDatepicker((prev) => { const arr = [...prev]; arr[index] = true; return arr; })
                }
                onCloseStartDatepicker={() =>
                  setOpenStartDatepicker((prev) => { const arr = [...prev]; arr[index] = false; return arr; })
                }
                openEndDatepicker={openEndDatepicker[index] || false}
                onOpenEndDatepicker={() =>
                  setOpenEndDatepicker((prev) => { const arr = [...prev]; arr[index] = true; return arr; })
                }
                onCloseEndDatepicker={() =>
                  setOpenEndDatepicker((prev) => { const arr = [...prev]; arr[index] = false; return arr; })
                }
                isReports={true}
              />
            ) : (
              <TextField
                value={filter.value}
                onChange={(e) => handleChange(index, "value", e.target.value)}
                size="small"
                placeholder="Insert a value"
                disabled={!filter.attribute || !filter.operator}
                sx={{ "& .MuiOutlinedInput-root": { height: 44 } }}
              />
            );

            const eyeButton = isStringAttribute(filter.attribute) ? (
              <IconButton
                onClick={() => {
                  if (!filter.attribute || !filter.operator) return;
                  getValuesForAttribute?.(
                    filter.attribute,
                    filter.operator as
                      | StringOperator
                      | NumericOperator
                      | DateOperator
                      | BooleanOperator
                      | ListOperator
                      | "",
                    filter.value,
                    index,
                  );
                }}
                sx={{ height: "44px", width: "44px", flexShrink: 0 }}
                disabled={!filter.operator || !filter.attribute}
              >
                <VisibilityIcon />
              </IconButton>
            ) : null;

            const minusButton = (
              <IconButton
                onClick={() => handleRemoveRow(index)}
                sx={{
                  bgcolor: "#FEF3F2",
                  borderColor: "#FDA29B",
                  borderRadius: "12px",
                  height: "44px",
                  width: "44px",
                  flexShrink: 0,
                  "&:hover": { bgcolor: "#FEF3F2" },
                }}
              >
                <RemoveOutlinedIcon sx={{ color: "#FDA29B" }} />
              </IconButton>
            );

            const attributeSelect = (
              <Stack spacing={1} sx={{ width: { xs: "calc(50% - 6px)", md: "30%" } }}>
                <Typography variant="subtitle2" color={primaryTextColor}>
                  User Attribute
                </Typography>
                <Select
                  value={filter.attribute}
                  onChange={(e) => handleChange(index, "attribute", e.target.value)}
                  IconComponent={ExpandMoreIcon}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected)
                      return <Typography sx={{ color: "#667085" }}>Select attribute</Typography>;
                    const attr = userAttributeOptions.find((a) => a.attributeKey === selected);
                    return attr ? attr.attributeName : selected;
                  }}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "transparent",
                    height: "44px",
                    "&:hover": { backgroundColor: "transparent" },
                    "& .MuiSelect-select": { display: "flex", alignItems: "center", padding: "8.5px 14px" },
                  }}
                  size="medium"
                >
                  {userAttributeOptions.map((attr) => (
                    <MenuItem key={attr.attributeKey} value={attr.attributeKey}>
                      {attr.attributeName}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            );

            const operatorSelect = (
              <Stack spacing={1} sx={{ width: { xs: "calc(50% - 6px)", md: "30%" } }}>
                <Typography variant="subtitle2" color={primaryTextColor}>
                  Operator
                </Typography>
                <Select
                  value={filter.operator}
                  onChange={(e) => handleChange(index, "operator", e.target.value)}
                  IconComponent={ExpandMoreIcon}
                  displayEmpty
                  disabled={!filter.attribute}
                  renderValue={(selected) => {
                    if (!selected)
                      return <Typography sx={{ color: "#6B7280" }}>Select an operator</Typography>;
                    const dataType = getSelectedAttribute(filter.attribute)?.dataType;
                    const pairs = getAttributeOperator(filter.attribute);
                    const match = pairs.find(([, val]) => val === selected);
                    const key = match?.[0] || String(selected);
                    return getEnumLabel(key, dataType);
                  }}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "transparent",
                    height: "44px",
                    "&:hover": { backgroundColor: "transparent" },
                    "& .MuiSelect-select": { display: "flex", alignItems: "center", padding: "8.5px 14px" },
                  }}
                  size="medium"
                >
                  {filter.attribute &&
                    getAttributeOperator(filter.attribute).map(([key, val]) => (
                      <MenuItem key={key} value={val}>
                        {getEnumLabel(key, getSelectedAttribute(filter.attribute)?.dataType)}
                      </MenuItem>
                    ))}
                </Select>
              </Stack>
            );

            return (
              <Box
                key={index}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  p: { xs: 2, md: 3 },
                  mb: 2,
                  backgroundColor: { xs: "#F9FAFB", md: "transparent" },
                }}
              >
                {/* Single row on desktop */}
                <Stack
                  direction="row"
                  gap={1.5}
                  alignItems="flex-end"
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  {attributeSelect}
                  {operatorSelect}
                  <Stack spacing={1} sx={{ width: "30%", flex: 1 }}>
                    <Typography variant="subtitle2" color={primaryTextColor}>Value</Typography>
                    {valueField}
                  </Stack>
                  {eyeButton}
                  {minusButton}
                </Stack>

                {/* Two rows on tablet */}
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Stack direction="row" gap={1.5} alignItems="flex-end">
                    {attributeSelect}
                    {operatorSelect}
                  </Stack>
                  <Stack direction="row" gap={1} alignItems="flex-end" mt={1.5}>
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color={primaryTextColor}>Value</Typography>
                      {valueField}
                    </Stack>
                    {eyeButton}
                    {minusButton}
                  </Stack>
                </Box>
              </Box>
            );
          })}

          <Button
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
            onClick={handleAddRow}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              color: "#344054",
              borderColor: "#D0D5DD",
              "&:hover": { borderColor: "#98A2B3", bgcolor: "transparent" },
            }}
          >
            Add filter
          </Button>

          <Stack spacing={1} mt={2}>
            <Typography variant="subtitle2" color={primaryTextColor}>
              User activity type
            </Typography>

            <Autocomplete
              multiple
              disabled={isActivityTypeDisabled()}
              options={
                userActionTypeOptions?.result.map((opt) => opt.key) || []
              }
              getOptionLabel={(key) => {
                const found = userActionTypeOptions?.result.find(
                  (opt) => opt.key === key,
                );
                return found?.value ?? key;
              }}
              filterOptions={(options, { inputValue }) => {
                const normalized = inputValue
                  .replace(/\s+/g, "")
                  .toLowerCase();
                return options.filter((key) => {
                  const label = (
                    userActionTypeOptions?.result.find((opt) => opt.key === key)
                      ?.value ?? key
                  )
                    .replace(/\s+/g, "")
                    .toLowerCase();
                  return label.includes(normalized);
                });
              }}
              value={value.activityType}
              size="small"
              onChange={(_, newValue) => onChange({ activityType: newValue })}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    value.activityType.length === 0
                      ? "Select user activity type"
                      : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 44,
                    },
                  }}
                  required
                />
              )}
              slotProps={{
                chip: {
                  sx: {
                    backgroundColor: "white",
                    padding: "14px 6px",
                    borderRadius: "6px",
                  },
                },
                popupIndicator: {
                  sx: { border: "none" },
                },
                clearIndicator: {
                  sx: { display: "none" },
                },
              }}
            />
          </Stack>
          <Stack spacing={1} mt={2}>
            <Typography variant="subtitle2" color={primaryTextColor}>
              User activity date
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              localeText={commonLocaleText}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{ width: "100%" }}
              >
                <DatePicker
                  label="Start date"
                  format={activityDateFormat}
                  value={fromDateValue}
                  open={openActivityStart}
                  onOpen={() => setOpenActivityStart(true)}
                  onClose={() => setOpenActivityStart(false)}
                  onChange={(date) =>
                    handleActivityDateChange("from", date as Dayjs | null)
                  }
                  disabled={!isUserActivityReport}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      onClick: () => isUserActivityReport && setOpenActivityStart(true),
                      InputLabelProps: { shrink: true },
                      placeholder: "Start date",
                      sx: {
                        "& .MuiPickersInputBase-root": {
                          height: 44,
                          padding: "0 12px",
                        },
                      },
                      InputProps: {
                        readOnly: true,
                        sx: {
                          "& .MuiInputAdornment-root button": {
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            "&:hover": { background: "transparent" },
                          },
                        },
                      },
                    },
                    openPickerButton: { sx: { border: "none" } },
                    openPickerIcon: { sx: { width: "15px" } },
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
                    "& .MuiPickersInputBase-root": {
                      flexDirection: "row-reverse",
                      padding: 0,
                      gap: "10px",
                    },
                    "& MuiInputAdornment-root": { padding: 0 },
                  }}
                />
                <Typography color="#98A2B3" fontSize="14px">
                  —
                </Typography>
                <DatePicker
                  label="End date"
                  format={activityDateFormat}
                  value={toDateValue}
                  open={openActivityEnd}
                  onOpen={() => setOpenActivityEnd(true)}
                  onClose={() => setOpenActivityEnd(false)}
                  onChange={(date) =>
                    handleActivityDateChange("to", date as Dayjs | null)
                  }
                  disabled={!isUserActivityReport}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "End date",
                      onClick: () => isUserActivityReport && setOpenActivityEnd(true),
                      InputLabelProps: { shrink: true },
                      sx: {
                        "& .MuiPickersInputBase-root": {
                          height: 44,
                          padding: "0 12px",
                        },
                      },
                      InputProps: {
                        readOnly: true,
                        sx: {
                          "& .MuiInputAdornment-root button": {
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            "&:hover": { background: "transparent" },
                          },
                        },
                      },
                    },
                    openPickerButton: { sx: { border: "none" } },
                    openPickerIcon: { sx: { width: "15px" } },
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiSvgIcon-root": { fontSize: "1.5rem" },
                    "& .MuiPickersInputBase-root": {
                      flexDirection: "row-reverse",
                      padding: 0,
                      gap: "10px",
                    },
                    "& MuiInputAdornment-root": { padding: 0 },
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
